import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Grid, Typography, CircularProgress } from '@mui/material';
import { ReactComponent as AutoLoginIcon } from '../images/autoLogin_icon.svg';
import { useTranslation } from 'react-i18next';
import PaperContainer from '../../authSimplified/components/PaperContainer';
import { simplifiedAuthStyles } from '../../authSimplified/styles/simplifiedAuth';
import CkEditorContent from '../../common/components/CkEditorContent';
import { useNavigate } from 'react-router-dom';
import { ErrorOutlineOutlined } from '@mui/icons-material';
import { useWelcomeContent } from '../../common/hooks/useWelcomeContent';
import { validateTextField } from '../../authentication/utils/validation';
import { SessionStore } from '../../constants';
import { FullPageLoader } from '../../common/components';
import { redirectUrlOnAuthentication } from '../../common/utils/redirectUrlOnAuthentication';
import { useAppSelector } from '../../store/hook';
import { getCookie } from '../../common/utils/cookies';
import { genericMessageHandler, a11yFocusApiErrors } from '../../common/utils/accessibilityGenericMessage';
import { StaticPageIconStyles } from '../../common/styles/staticPageIcon';
import { handleRegisterAssessmentForTCPlus } from '../../authSimplified/utils/loginSuccessHandler';
import { DpnCheckbox } from '../../authSimplified/components/DpnCheckbox';
import { DPNAcceptance } from '../../authSimplified/interfaces/SimplifiedAuthInputFields';

const AutoLoginPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const autoLoginErrorMessageRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { classes: StaticPageIconClasses } = StaticPageIconStyles();
  const template = useAppSelector((state) => state.template);
  const candidate = useAppSelector((state) => state.candidate);
  const workflowSession = useAppSelector((state) => state.workflowSession);
  const currentWorkflowItem = useAppSelector((state) => state.currentWorkflowItem);
  const [dpnDetails, setDpnDetails] = useState<DPNAcceptance>({
    dpnAcceptance: 'false',
    dpnAcceptanceHelperText: '',
  });
  const welcomeContent = useWelcomeContent();
  const companySettings = useAppSelector((state) => state.companySettings);
  const { classes } = simplifiedAuthStyles();
  const isBasicFlow = getCookie('BasicFlow') === '1';
  const [errorAlert, setErrorAlert] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isRedirectionLoading, setIsRedirectionLoading] = useState<boolean>(false);

  const handleDpnAcceptance = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked;
    const name = event.target.name;
    const updatedFieldValues = validateTextField(name, checked);
    setDpnDetails({ ...dpnDetails, ...updatedFieldValues });
  };

  const autoLoginRedirectionOnDpnOff = async (): Promise<void> => {
    setIsRedirectionLoading(true);
    sessionStorage.setItem(SessionStore.dpnAccepted, 'true');
    const data = await handleRegisterAssessmentForTCPlus(candidate, workflowSession);
    if (data.url) {
      navigate(data.url);
      return;
    }

    const url = await redirectUrlOnAuthentication(currentWorkflowItem, { template, candidate });
    if (url) {
      navigate(url);
    }
    setIsRedirectionLoading(false);
  };

  const handleContinueClick = (event: React.SyntheticEvent): void => {
    event.preventDefault();
    const dpnAcceptanceHelperValues = validateTextField('dpnAcceptance', dpnDetails.dpnAcceptance);

    if (companySettings?.showDpn && dpnAcceptanceHelperValues.dpnAcceptanceHelperText !== '') {
      setDpnDetails({
        ...dpnDetails,
        dpnAcceptanceHelperText: dpnAcceptanceHelperValues.dpnAcceptanceHelperText,
      });
      genericMessageHandler();
      return;
    }
    sessionStorage.setItem(SessionStore.dpnAccepted, 'true');
    (async (): Promise<void> => {
      try {
        setLoading(true);
        const data = await handleRegisterAssessmentForTCPlus(candidate, workflowSession);
        if (data.url) {
          navigate(data.url);
          return;
        }

        if (data.error) {
          setErrorAlert(data.error);
          a11yFocusApiErrors(autoLoginErrorMessageRef);
          return;
        }

        const url = await redirectUrlOnAuthentication(currentWorkflowItem, { template, candidate });
        if (url) {
          navigate(url);
        }
      } catch (e) {
        setErrorAlert('genericError');
        console.error('error occurred while continuing for autologin flow', e);
      } finally {
        setLoading(false);
      }
    })();
  };

  useEffect(() => {
    if (!isBasicFlow && !companySettings.showDpn && template.id) {
      autoLoginRedirectionOnDpnOff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id, companySettings.showDpn, isBasicFlow]);

  const { dpnAcceptance, dpnAcceptanceHelperText } = dpnDetails;

  return (
    <>
      <FullPageLoader isLoading={isRedirectionLoading} />
      {!welcomeContent.welcomePageHeading && !welcomeContent.welcomePageContent && <FullPageLoader isLoading />}
      {!isRedirectionLoading && (
        <PaperContainer variant="medium">
          <Grid container justifyContent="center">
            <Box className={classes.autoLogin}>
              <Box textAlign="center">
                {isBasicFlow && (
                  <AutoLoginIcon width="120px" height="120px" className={StaticPageIconClasses.staticPageIcon} />
                )}
                <Typography variant="h1">{`${t(welcomeContent.welcomePageHeading)}${
                  welcomeContent.isDefault && isBasicFlow ? '!' : ''
                }`}</Typography>
                <Box marginTop={1.5}>
                  <CkEditorContent
                    content={
                      welcomeContent.isDefault
                        ? t(welcomeContent.welcomePageContent)
                        : welcomeContent.welcomePageContent
                    }
                    pageStartTime={new Date()}
                    generalContentName={'WELCOME_PAGE'}
                  />
                </Box>
              </Box>
              {/* DPN acceptance checkbox */}
              <Box display="flex" justifyContent="center">
                {companySettings?.showDpn && (
                  <DpnCheckbox
                    layoutCode={template.layoutCode}
                    dpnAcceptance={dpnAcceptance}
                    dpnAcceptanceHelperText={dpnAcceptanceHelperText}
                    dataTestId="autoLoginDpnAcceptance"
                    handleDpnAcceptance={handleDpnAcceptance}
                  />
                )}
              </Box>
              {errorAlert !== '' && (
                <div
                  ref={autoLoginErrorMessageRef}
                  tabIndex={0}
                  className={`${classes.marginBottom15} ${classes.customError}`}>
                  <ErrorOutlineOutlined />
                  &nbsp;{t(errorAlert)}
                </div>
              )}
              <Box textAlign="center">
                {/* Continue button */}
                <Button
                  type="submit"
                  variant="outlined"
                  color="primary"
                  disabled={loading}
                  aria-hidden={loading}
                  aria-disabled="false"
                  onClick={handleContinueClick}
                  aria-label={t('continue')}
                  data-testid="autoLoginContinue">
                  {t('continue')}
                  {loading && <CircularProgress size={25} className={classes.buttonProgress} />}
                </Button>
              </Box>
            </Box>
          </Grid>
        </PaperContainer>
      )}
    </>
  );
};

export default AutoLoginPage;
